/**
 * Hire-purchase style payment receipt (rectangular paper form).
 * Ink / border color follows the workspace theme.
 */
import { exportStandaloneHtmlToPdf } from './exportReports';
import {
  companyDisplayName,
  companyComposedAddress,
  readCompanyProfile,
} from './companyProfile';
import { mediaSrc } from './mediaUrl';
import { getPdfThemeColors } from './themeSettings';

export type PaymentReceiptInput = {
  receiptNo: string;
  receiptDate: string;
  customerName: string;
  vehicleNo?: string;
  haNumber?: string;
  amount: number;
  amountInWords: string;
  paymentMode: string;
  paymentDated?: string;
  towards: string;
  companyName?: string;
  companyPhone?: string;
  companyAddress?: string;
  partnerLabel?: string;
};

const ONES = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function twoDigits(n: number): string {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return `${TENS[t]}${o ? ` ${ONES[o]}` : ''}`.trim();
}

/** Indian numbering: thousand / lakh / crore. */
export function amountInIndianWords(amount: number): string {
  const n = Math.round(Math.max(0, Number(amount) || 0));
  if (n === 0) return 'Zero Rupees Only';
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const hundred = Math.floor((n % 1000) / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (crore) parts.push(`${twoDigits(crore)} Crore`);
  if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
  if (hundred) parts.push(`${ONES[hundred]} Hundred`);
  if (rest) parts.push(twoDigits(rest));
  return `${parts.join(' ')} Rupees Only`;
}

export function formatReceiptDate(iso: string): string {
  const ymd = String(iso || '').trim().slice(0, 10);
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return String(iso || '—');
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function mixHex(hex: string, towardWhite: number): string {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const n = towardWhite;
  const ch = (i: number) => {
    const v = parseInt(h.slice(i, i + 2), 16);
    return Math.round(v + (255 - v) * n)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${ch(0)}${ch(2)}${ch(4)}`;
}

/** Label + ruled value line (underlines share a common left edge via CSS grid). */
function field(label: string, value: string): string {
  const val = value && value !== '—' ? esc(value) : '&nbsp;';
  return `<div class="fld">
      <span class="lbl">${esc(label)}</span>
      <span class="val">${val}</span>
    </div>`;
}

/** Full HTML document — rectangular finance receipt, theme-colored. */
export function buildPaymentReceiptHtml(input: PaymentReceiptInput): string {
  const profile = readCompanyProfile();
  const theme = getPdfThemeColors();
  const ink = theme.brandStrong || theme.brand || '#1d4ed8';
  const inkSoft = mixHex(ink, 0.5);
  const inkPale = mixHex(ink, 0.88);
  const companyRaw = input.companyName || companyDisplayName(profile) || 'FINANCE';
  const company = esc(companyRaw);
  const phone = esc(input.companyPhone || profile.phone || profile.altPhone || '');
  const address = esc(input.companyAddress || companyComposedAddress(profile) || '');
  const partner = esc(
    input.partnerLabel ||
      profile.partners?.[0]?.role ||
      (profile.partners?.[0]?.name ? 'Mg. Partner' : 'Authorized Signatory')
  );
  const logo = mediaSrc(profile.logoUrl || '');
  const amt = Math.round(Number(input.amount) || 0).toLocaleString('en-IN');
  const dated = formatReceiptDate(input.paymentDated || input.receiptDate);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Finance Receipt — ${company}</title>
  <style>
    @page { size: A5 portrait; margin: 5mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { background: #cbd5e1; }
    body {
      font-family: "Times New Roman", Times, Georgia, serif;
      color: ${ink};
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .page {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 10px;
    }
    /* Rectangular slip — A5-width paper proportions */
    .sheet {
      width: 148mm;
      min-height: 200mm;
      max-width: 100%;
      background: #fff;
      border: 2.75px solid ${ink};
      box-shadow: inset 0 0 0 1.25px ${inkSoft};
      padding: 9px 14px 10px;
      position: relative;
      display: flex;
      flex-direction: column;
    }
    .sheet::before {
      content: "";
      position: absolute;
      inset: 5px;
      border: 1px solid ${inkSoft};
      pointer-events: none;
    }
    .head {
      text-align: center;
      padding: 2px 4px 8px;
      border-bottom: 1.5px solid ${ink};
      margin-bottom: 6px;
      position: relative;
      z-index: 1;
    }
    .logo {
      max-height: 32px;
      max-width: 120px;
      object-fit: contain;
      margin: 0 auto 4px;
      display: block;
    }
    .oval {
      display: inline-block;
      margin: 0 auto 5px;
      padding: 6px 22px;
      border: 2.25px solid ${ink};
      border-radius: 999px;
      box-shadow: inset 0 0 0 1.25px ${inkSoft};
      background: linear-gradient(180deg, ${inkPale} 0%, #fff 60%);
    }
    .oval .name {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 15px;
      font-weight: 800;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: ${ink};
      line-height: 1.15;
      white-space: nowrap;
    }
    .co-title {
      margin: 0;
      font-size: 22px;
      font-weight: 800;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: ${ink};
      line-height: 1.1;
    }
    .badge {
      display: inline-block;
      margin-top: 5px;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.32em;
      color: #fff;
      background: ${ink};
      border: 1px solid ${ink};
      padding: 3px 14px;
    }
    .meta {
      margin-top: 5px;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 10px;
      line-height: 1.4;
      color: ${ink};
      font-weight: 600;
    }
    .body {
      flex: 1;
      padding: 2px 2px 0;
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
    }
    .grid2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: 16px;
      align-items: end;
    }
    /* Label sits tight left; value underline fills the rest (no overflow clash) */
    .fld {
      display: flex;
      align-items: flex-end;
      gap: 6px;
      margin-top: 7px;
      min-height: 18px;
    }
    .lbl {
      flex: 0 0 auto;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      font-weight: 700;
      color: ${ink};
      white-space: nowrap;
      padding-bottom: 2px;
      text-align: left;
    }
    .val {
      flex: 1 1 auto;
      min-width: 0;
      border-bottom: 1.5px solid ${ink};
      color: #0f172a;
      font-size: 12.5px;
      font-weight: 700;
      font-family: Arial, Helvetica, sans-serif;
      padding: 0 2px 2px;
      line-height: 1.2;
      min-height: 18px;
      word-break: break-word;
    }
    .amt-row {
      display: flex;
      justify-content: flex-end;
      margin-top: 14px;
    }
    .rs-box {
      border: 2px solid ${ink};
      background: ${inkPale};
      min-width: 148px;
      padding: 7px 12px;
      text-align: right;
      font-family: Arial, Helvetica, sans-serif;
    }
    .rs-box .tag {
      display: block;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.1em;
      color: ${ink};
      text-transform: uppercase;
    }
    .rs-box .num {
      font-size: 19px;
      font-weight: 800;
      color: #0f172a;
      line-height: 1.15;
    }
    .sigs {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
      margin-top: auto;
      padding-top: 12px;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      color: ${ink};
    }
    .sig-block { text-align: center; }
    .for-co {
      font-weight: 800;
      margin-bottom: 2px;
      font-size: 11.5px;
    }
    .sig-space { height: 36px; }
    .sig-cap {
      border-top: 1.5px solid ${ink};
      padding-top: 4px;
      font-weight: 700;
    }
    .foot {
      margin-top: 8px;
      text-align: center;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 8.5px;
      color: ${inkSoft};
      letter-spacing: 0.03em;
      position: relative;
      z-index: 1;
    }
    @media print {
      html, body { background: #fff; }
      .page { padding: 0; min-height: auto; }
      .sheet {
        width: 100%;
        min-height: 190mm;
        box-shadow: inset 0 0 0 1.25px ${inkSoft};
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="sheet">
      <header class="head">
        ${logo ? `<img class="logo" src="${esc(logo)}" alt="" />` : ''}
        <div class="oval"><div class="name">${company}</div></div>
        <div class="co-title">${company}</div>
        <div class="badge">FINANCE RECEIPT</div>
        <div class="meta">
          ${phone ? `<div>Office: ${phone}</div>` : ''}
          ${address ? `<div>${address}</div>` : ''}
        </div>
      </header>

      <div class="body">
        <div class="grid2">
          ${field('No.', input.receiptNo)}
          ${field('Date', formatReceiptDate(input.receiptDate))}
        </div>
        ${field('Received from Sri', input.customerName)}
        <div class="grid2">
          ${field('Vehicle No.', input.vehicleNo || '')}
          ${field('H.P. No.', input.haNumber || '')}
        </div>
        ${field('the sum of Rupees', input.amountInWords)}
        <div class="grid2">
          ${field('Cash / D.D.', input.paymentMode)}
          ${field('Dated', dated)}
        </div>
        ${field('towards', input.towards)}

        <div class="amt-row">
          <div class="rs-box">
            <span class="tag">Rs.</span>
            <div class="num">${esc(amt)}/-</div>
          </div>
        </div>

        <div class="sigs">
          <div class="sig-block">
            <div class="sig-space"></div>
            <div class="sig-cap">Signature of the Payer</div>
          </div>
          <div class="sig-block">
            <div class="for-co">For ${company}</div>
            <div class="sig-space"></div>
            <div class="sig-cap">${partner}</div>
          </div>
        </div>
      </div>

      <div class="foot">Computer-generated finance receipt · ${esc(companyRaw)}</div>
    </div>
  </div>
</body>
</html>`;
}

/** Open the browser print dialog for this receipt. */
export function printPaymentReceipt(input: PaymentReceiptInput): void {
  const html = buildPaymentReceiptHtml(input);
  const win = window.open('', '_blank');
  if (!win) {
    window.alert('Allow pop-ups to print the receipt.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  const run = () => {
    win.focus();
    win.print();
  };
  win.addEventListener('load', run, { once: true });
  window.setTimeout(run, 400);
}

/** Download a PDF of the same rectangular theme-colored receipt. */
export async function downloadPaymentReceiptPdf(input: PaymentReceiptInput): Promise<void> {
  const company = input.companyName || companyDisplayName(readCompanyProfile()) || 'Finance';
  const html = buildPaymentReceiptHtml(input);
  await exportStandaloneHtmlToPdf(`Finance Receipt — ${company} — ${input.receiptNo}`, html, {
    widthPx: 560,
    captureSelector: '.sheet',
    format: 'a5',
  });
}
